import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { KioskProductOverviewModalConfig as KioskProductOverviewModalContentConfig } from '@front-platform/content-builder';
import { kioskProductOverviewModalResolveConfig } from '@front-platform/content-builder';
import { Component, OnInit, input, computed } from '@angular/core';
import { ModalStoreSelector } from '@common/selectors/modal.selector';
import { CartItemKeys } from '@config/keys/cart.keys';
import { AdditionGroupKeys } from '@config/keys/shop.keys';
import { ProductTypeValues } from '@config/values/product.values';
import { IAllergens } from '@core/models/allergens.model';
import { ICartItem, ICartDeclinableSeparateAdditions } from '@core/models/cart.model';
import { DoodProductModel, ISelectedCompoundItem } from '@core/models/product.model';
import { EventTrackerService } from '@core/services/event-tracker/event-tracker.service';
import { ModalsService } from '@core/services/modals/modals.service';
import { combineLatest, distinctUntilChanged, filter, map, Subject, take, takeUntil } from 'rxjs';
import { ProductStoreSelector } from '@common/selectors/product.selector';
import { DeclinableStoreSelector } from '@common/selectors/declinable.selector';
import { CartService } from '@core/services/cart/cart.service';
import { ProductService } from '@core/services/product/product.service';
import { ProductStoreRefiner } from '@common/refiners/product.refiner';
import { IAdditionGroup } from '@core/models/shop.model';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { ModalConfig, GetModalInterface } from '@core/services/modals/modals-util.type';
import { toSignal } from '@angular/core/rxjs-interop';

/**
 * Sales-booster popups (upsell, cross-sell, BOGOF) use a fixed z-index of 750
 * so they stay above the product-add modal. modal-filler-atom paints at
 * `index + 50`, so this index places the overview above those popups.
 */
export const KIOSK_PRODUCT_OVERVIEW_ABOVE_SALES_BOOSTER_INDEX = 760;

export type KioskProductOverviewModalConfig = ModalConfig<{
  handle: 'kiosk-add-product-overview-modal';
  inputs: {
    replaceAddToCart?: (product: ICartItem) => void;
    onCartItemAdded?: (cartItem: ICartItem) => void;
    navigateToProduct?: () => void;
    image?: string;
    name?: string;
    price?: string;
    description?: string;
    allergens?: IAllergens[];
    type?: string;
    /** Overrides the modal-stack index used for z-index. See KIOSK_PRODUCT_OVERVIEW_ABOVE_SALES_BOOSTER_INDEX. */
    zIndex?: number;
    colors?: {
      background?: IContentBuilderFieldColor;
      accent?: IContentBuilderFieldColor;
      closeButton?: IContentBuilderFieldColor;
    };
  };
}>;
export interface KioskProductOverviewModalRuntimeConfig {
  'replace_add_to_cart'?: (product: ICartItem) => void;
  'on_cart_item_added'?: (cartItem: ICartItem) => void;
  'navigate_to_product'?: () => void;
  'image'?: string;
  'name'?: string;
  'price'?: string;
  'description'?: string;
  'allergens'?: IAllergens[];
  'type'?: string;
  'z_index'?: number;
  'colors'?: {
    background?: IContentBuilderFieldColor;
    accent?: IContentBuilderFieldColor;
    closeButton?: IContentBuilderFieldColor;
  };
}

export type KioskProductOverviewModalConfigInput = Partial<
  Omit<KioskProductOverviewModalContentConfig, keyof KioskProductOverviewModalRuntimeConfig> &
    KioskProductOverviewModalRuntimeConfig
>;
export type KioskProductOverviewModalResolvedConfig = Omit<
  KioskProductOverviewModalContentConfig,
  keyof KioskProductOverviewModalRuntimeConfig
> &
  Partial<KioskProductOverviewModalRuntimeConfig>;
export interface KioskProductOverviewModalContract extends ConfigComponentContract<
  KioskProductOverviewModalConfigInput,
  KioskProductOverviewModalResolvedConfig
> {}
@Component({
  selector: 'app-kiosk-product-overview-modal',
  templateUrl: 'kiosk-product-overview-modal.component.html',
  standalone: false,
})
export class KioskProductOverviewModalComponent
  implements
    OnInit,
    GetModalInterface<KioskProductOverviewModalConfig>,
    KioskProductOverviewModalContract
{
  readonly config = input<KioskProductOverviewModalConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    KioskProductOverviewModalContentConfig,
    KioskProductOverviewModalRuntimeConfig
  >(kioskProductOverviewModalResolveConfig, () => ({ config: this.config() }));
  static handle = 'kiosk-add-product-overview-modal' as const;

  index = toSignal(
    this.modalSelector
      .selectModal(KioskProductOverviewModalComponent.handle)
      .pipe(map(el => (el?.index ? el.index : 4))),
    { initialValue: 0 },
  );

  product$ = this.productSelector.selectActive$;
  declinables$ = this.declinableSelector.selectDeclinables$;

  readonly replaceAddToCart = computed(
    () => this.resolvedConfig()['replace_add_to_cart'] as unknown as (product: ICartItem) => void,
  );
  readonly onCartItemAdded = computed(
    () => this.resolvedConfig()['on_cart_item_added'] as unknown as (cartItem: ICartItem) => void,
  );
  readonly navigateToProduct = computed(
    () => this.resolvedConfig()['navigate_to_product'] as unknown as () => void,
  );
  readonly image = computed(() => this.resolvedConfig()['image'] as unknown as string);
  readonly name = computed(() => this.resolvedConfig()['name'] as unknown as string);
  readonly price = computed(() => this.resolvedConfig()['price'] as unknown as string);
  readonly description = computed(() => this.resolvedConfig()['description'] as unknown as string);
  readonly allergens = computed(
    () => this.resolvedConfig()['allergens'] as unknown as IAllergens[],
  );
  readonly type = computed(() => this.resolvedConfig()['type'] as unknown as ProductTypeValues);
  readonly zIndex = computed(() => this.resolvedConfig()['z_index']);
  /** Stack index used by the backdrop and the panel. A forced z_index keeps this modal above sales-booster popups. */
  readonly overlayIndex = computed(() => {
    const forced = this.zIndex();
    return typeof forced === 'number' ? forced : (this.index() ?? 0);
  });
  readonly colors = computed(
    () =>
      this.resolvedConfig()['colors'] as unknown as {
        background?: IContentBuilderFieldColor;
        accent?: IContentBuilderFieldColor;
        closeButton?: IContentBuilderFieldColor;
      },
  );

  static onAdd?: () => void;

  quantity: number = 1;

  selectedAdditions: string[] = [];
  selectedAdditionsByGroup: (IAdditionGroup & {
    selectedAdditions: string[];
  })[] = [];

  private _destroyerRef = new Subject<boolean>();
  product: DoodProductModel | null = null;

  constructor(
    private readonly modalSelector: ModalStoreSelector,
    private readonly modalsService: ModalsService,
    private readonly productSelector: ProductStoreSelector,
    private readonly declinableSelector: DeclinableStoreSelector,
    private readonly cartService: CartService,
    private readonly productService: ProductService,
    private readonly eventTrackerService: EventTrackerService,
    private readonly productRefiner: ProductStoreRefiner,
  ) {}

  ngOnInit(): void {
    this.productRefiner.selectAdditionsByGroups
      .pipe(distinctUntilChanged(), takeUntil(this._destroyerRef))
      .subscribe(_additions => {
        this.selectedAdditionsByGroup = [];
        _additions.map(el => {
          if (el.id) {
            this.selectedAdditionsByGroup.push({
              ...el,
              selectedAdditions: [],
            });
          }
        });
      });

    this.productSelector.selectActive$
      .pipe(
        filter(_product => !!_product),
        take(1),
      )
      .subscribe(product => {
        this.product = product;
        if (product) {
          this.eventTrackerService.dispatch(EventTrackerService.EventProductViewed, product);
        }
      });
  }

  close(): void {
    this.modalsService.close(KioskProductOverviewModalComponent.handle);
  }

  addToCart(): void {
    if (this.replaceAddToCart()) {
      this.product$.pipe(take(1)).subscribe(product => {
        const replaceAddToCart = this.replaceAddToCart();
        if (replaceAddToCart && product) {
          const cartItem: Partial<ICartItem> = {
            [CartItemKeys.ItemId]: product?.id,
            [CartItemKeys.ShopId]: product?.shop_id,
            [CartItemKeys.Additions]: this.selectedAdditions,
            [CartItemKeys.Quantity]: this.quantity,
          };

          replaceAddToCart(cartItem as ICartItem);
        }
      });
      this.close();

      return;
    }

    combineLatest([this.product$, this.declinableSelector.selectDeclinables$])
      .pipe(
        take(1),
        map(([product, compoundItems]) => {
          if (product?.type === ProductTypeValues.Declinable) {
            const declinable: Partial<ICartItem> = {
              [CartItemKeys.Products]: [],
              [CartItemKeys.ItemId]: product.id,
              [CartItemKeys.ShopId]: product.shop_id,
              [CartItemKeys.Quantity]: this.quantity,
            };
            compoundItems.map(item => {
              if (!item.isValid) {
                this.productService.setInvalidCompoundProduct(item.store_id);
              } else {
                item.selected?.forEach((e: ISelectedCompoundItem) => {
                  const additions: string[] = [];
                  const separateAdditionsByGroups: ICartDeclinableSeparateAdditions[] = [];
                  e.additions_by_group?.forEach(group => {
                    group.selected?.map(el => additions.push(el));
                    separateAdditionsByGroups.push({
                      id: group[AdditionGroupKeys.Id],
                      selected: group.selected as string[],
                    });
                  });
                  declinable[CartItemKeys.Products]?.push({
                    [CartItemKeys.Id]: e.id,
                    [CartItemKeys.Additions]: additions,
                    [CartItemKeys.Quantity]: e.quantity,
                    [CartItemKeys.StoreId]: item.store_id,
                    [CartItemKeys.ShopId]: product.shop_id,
                    [CartItemKeys.SeparateAdditionsByGroup]: separateAdditionsByGroups,
                  });
                });
              }
            });
            this.cartService
              .addCartItem$(declinable as ICartItem)
              .subscribe(this.onCartItemAdded());
            this.eventTrackerService.dispatch(EventTrackerService.EventProductAddedToCart, {
              product,
              quantity: this.quantity,
            });
            if (KioskProductOverviewModalComponent.onAdd) {
              KioskProductOverviewModalComponent.onAdd();
            }
            this.close();
            return;
          }
          // simple product
          const cartItem: Partial<ICartItem> = {
            [CartItemKeys.ItemId]: product?.id,
            [CartItemKeys.ShopId]: product?.shop_id,
            [CartItemKeys.Additions]: this.selectedAdditions,
            [CartItemKeys.Quantity]: this.quantity,
          };

          this.cartService.addCartItem$(cartItem as ICartItem).subscribe(this.onCartItemAdded());
          this.eventTrackerService.dispatch(EventTrackerService.EventProductAddedToCart, {
            product,
            quantity: this.quantity,
          });

          if (KioskProductOverviewModalComponent.onAdd) {
            KioskProductOverviewModalComponent.onAdd();
          }
          this.close();
        }),
      )
      .subscribe();
  }

  onNavigateToProduct(): void {
    this.navigateToProduct()();
    this.close();
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(KioskProductOverviewModalComponent, {
  'keyAliases': {},
});
